import React, { useMemo } from "react";
import { longDateString } from "../../utils";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import "../../starek.css";

function PartsCardView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    onAdd,
    onRowClicked,
    onToggleActive,
    togglingActiveId,
}) {
    const isPartSelected = (partId) => {
        return selectedRows.some((row) => row.id === partId);
    };

    const handleCheckboxChange = (part) => {
        let newSelectedRows;
        if (isPartSelected(part.id)) {
            newSelectedRows = selectedRows.filter((row) => row.id !== part.id);
        } else {
            newSelectedRows = [...selectedRows, part];
        }
        if (onSelectedRowsChange) {
            onSelectedRowsChange({ selectedRows: newSelectedRows });
        }
    };

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    if (filteredItems.length === 0) {
        return (
            <EntityEmptyState
                icon="fa-cube"
                entityName="Part"
                messages={EMPTY_MESSAGES.parts}
                isFiltered={!!filterText?.trim()}
                onAdd={onAdd}
            />
        );
    }

    return (
        <div className="row g-3">
            {filteredItems.map((part) => (
                <div key={part.id} className="col-lg-3 col-md-4 col-sm-6">
                    <div
                        className={`card h-100 shadow-sm ${
                            isPartSelected(part.id)
                                ? "border-primary border-2"
                                : ""
                        }`}
                        style={{ cursor: "pointer", opacity: part.is_active === false ? 0.55 : 1 }}
                        onClick={() => onRowClicked?.(part)}
                    >
                        <div className="card-header bg-primary text-white">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center flex-grow-1">
                                    <div className="form-check mb-0 me-2" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={isPartSelected(part.id)}
                                            onChange={() =>
                                                handleCheckboxChange(part)
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={`Select ${part.name}`}
                                        />
                                    </div>
                                    <h5
                                        className="card-title mb-0 text-truncate"
                                        title={part.name}
                                    >
                                        {part.name}
                                    </h5>
                                </div>
                                <div className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    {onToggleActive && (
                                        <button
                                            className={`btn btn-sm ${part.is_active ? "btn-outline-light" : "btn-outline-warning"}`}
                                            style={{ padding: "2px 6px", fontSize: "0.7rem" }}
                                            title={part.is_active ? "Deactivate" : "Restore"}
                                            disabled={togglingActiveId === part.id}
                                            onClick={() => onToggleActive(part)}
                                        >
                                            {togglingActiveId === part.id
                                                ? <span className="spinner-border spinner-border-sm" />
                                                : <i className={`fa ${part.is_active ? "fa-ban" : "fa-undo"}`} />}
                                        </button>
                                    )}
                                    {part.image_data_url ? (
                                        <img
                                            src={part.image_data_url}
                                            alt={part.name}
                                            style={{ width: 32, height: 32, objectFit: "cover", borderRadius: "50%" }}
                                        />
                                    ) : (
                                        <i className="fa fa-cube fa-lg"></i>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="card-body d-flex flex-column">
                            <div className="d-flex align-items-start mb-3">
                                <div className="roundel roundel-md me-3">
                                    {part.image_asset?.url ? (
                                        <img
                                            src={part.image_asset.url}
                                            alt={part.name || "Part"}
                                        />
                                    ) : (
                                        <i className="fa fa-cube fa-lg text-muted"></i>
                                    )}
                                </div>
                                <p
                                    className="text-muted mb-0 flex-grow-1"
                                    style={{ fontSize: "0.875rem" }}
                                >
                                    {part.description || "No description"}
                                </p>
                            </div>
                            <div className="border-top pt-2 mt-auto">
                                <small className="text-muted d-block">
                                    <i className="fas fa-user me-1"></i>
                                    {part.updated_by?.name || "Unknown"}
                                </small>
                                <small className="text-muted d-block mt-1">
                                    <i className="fas fa-clock me-1"></i>
                                    {longDateString(part.updated_at)}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default PartsCardView;
